import { t } from 'commons/libs/i18n'
import {
  formatDate,
  formatDatePeriod,
  getDiffBetweenDates,
  getEndOfDay,
  getToday,
  isDateBefore,
} from 'commons/utils/date'
import { Flex, type HeadItemType, Icon, SlimButton, Table } from 'components'
import { PaginationV2 } from 'components/Table/PaginationV2'
import { TableWrapper } from 'container/SalesCalendar/styles'
import type { SalesCalendarListItem } from 'services/salesCalendar/interfaces'
import {
  setPage,
  setPageSize,
  toggleAllCalendarsChecked,
  toggleCalendarChecked,
} from 'store/SalesCalendarContext/salesCalendarSlice'
import { useDispatch, useSelector } from 'store/store'

export type CalendarsTableProps = {
  onDelete: (item: SalesCalendarListItem) => void
  onEdit: (item: SalesCalendarListItem) => void
}

export const CalendarsTable = ({ onDelete, onEdit }: CalendarsTableProps) => {
  const dispatch = useDispatch()
  const {
    calendars,
    loadingSaveCalendar,
    pagination: { currentPage, pageSize, totalItems },
  } = useSelector((state) => state.salesCalendar)

  const tableLabels: HeadItemType[] = [
    { columnName: 'fairShare', label: t('screens.salesCalendar.fairShare') },
    { columnName: 'portalWindow', label: t('screens.salesCalendar.portalWindow') },
    { columnName: 'approvalND', label: t('screens.salesCalendar.approvalND') },
    { columnName: 'havaWindow', label: t('screens.salesCalendar.havaWindow') },
    { columnName: 'actions', label: '' },
  ]

  const shouldDisableEdit = (item: SalesCalendarListItem) => {
    const { endDatePortal, endDateHava, endDateApproval } = item
    const validEndDates = [endDatePortal, endDateHava, endDateApproval].filter(Boolean)
    const dateDiffs = validEndDates.map((date) => getDiffBetweenDates(date, getToday(), 'days'))
    return dateDiffs.every((diff) => diff > 90)
  }

  const shouldDisableDelete = (item: SalesCalendarListItem) => {
    const { endDatePortal, endDateHava, endDateApproval } = item
    const validEndDates = [endDatePortal, endDateHava, endDateApproval].filter(Boolean)
    return validEndDates.every((date) =>
      isDateBefore(getEndOfDay(date, 'YYYY-MM-DDTHH:mm:ss'), getToday()),
    )
  }

  const tableData = () => {
    return calendars.map((item) => {
      const id = `${item.officeCode}-${item.yearMonth}`

      const fairShareLabel = formatDate(String(item.yearMonth), 'MM/YYYY', 'YYYYMM')
      const portalWindowLabel =
        formatDatePeriod(item.initialDatePortal, item.endDatePortal, t('common.until')) || '-'
      const havaWindowLabel =
        formatDatePeriod(item.initialDateHava, item.endDateHava, t('common.until')) || '-'
      const approvalNDLabel =
        formatDatePeriod(item.initialDateApproval, item.endDateApproval, t('common.until')) || '-'

      return {
        checked: item.checked ?? false,
        content: [
          fairShareLabel,
          portalWindowLabel,
          approvalNDLabel,
          havaWindowLabel,
          <Flex alignItems="center" direction="row" gap="sm" justifyContent="flex-end">
            <SlimButton
              content={t('buttons.edit')}
              disabled={loadingSaveCalendar || shouldDisableEdit(item)}
              icon={<Icon icon="pencil" size="15" />}
              key={`edit-${id}`}
              onClick={() => onEdit(item)}
              variant="border"
            />
            <SlimButton
              disabled={loadingSaveCalendar || shouldDisableDelete(item)}
              icon={<Icon icon="trash" size="18" />}
              key={`delete-${id}`}
              onClick={() => onDelete(item)}
              variant="border"
            />
          </Flex>,
        ],
        id,
      }
    })
  }

  const setChecked = (checked: boolean, id?: string) => {
    if (!id) {
      dispatch(toggleAllCalendarsChecked(checked))
    } else {
      dispatch(toggleCalendarChecked(id))
    }
  }

  return (
    <TableWrapper>
      <Table
        bodyData={tableData()}
        checkboxSize="small"
        distributeWidth={true}
        hasCheckbox
        headData={tableLabels}
        rowHeight={48}
        setChecked={setChecked}
      />
      <PaginationV2
        currentPage={currentPage}
        itemsPerPage={pageSize}
        itemsPerPageOptions={[12, 24, 36, 48, 60]}
        onItemsPerPageChange={(size) => dispatch(setPageSize(size))}
        onPageChange={(page) => dispatch(setPage(page))}
        totalItems={totalItems}
      />
    </TableWrapper>
  )
}
